import { extendZodWithOpenApi } from "@asteasolutions/zod-to-openapi";
import { mongoose } from "@typegoose/typegoose";
import { z } from "zod";
import { toLoraEui } from "../utils/shortCodes/EuiShortCodeMapper.js";
import { verdiIdRegex } from "../utils/shortCodes/shortCodesLookup.js";

extendZodWithOpenApi(z);

/**
 * Zod schema for a stored UUID `node_id`: lowercase 8-4-4-4-12. The one canonical form every UUID version
 * shares, so no per-version variant is needed. Uppercase is rejected; callers lowercase first.
 */
export const UuidString = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, {
        message: "Must be a lowercase UUID (8-4-4-4-12)",
    })
    .openapi({
        type: "string",
        pattern: "^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$",
        description: "UUID (lowercase 8-4-4-4-12)",
        example: "b3536c25-6e6e-4f9e-9d1a-6f0b6f0b6f0b",
    });

/** Values accepted by {@link UuidString}. */
export type Uuid = z.infer<typeof UuidString>;

/**
 * Zod schema for a vendor-defined identifier. No assumed shape beyond being present.
 */
export const VendorOpaqueIdString = z.string().min(1).openapi({
    type: "string",
    minLength: 1,
    description: "Vendor-defined identifier",
    example: "18000294799",
});

/** Values accepted by {@link VendorOpaqueIdString}. */
export type VendorOpaqueId = z.infer<typeof VendorOpaqueIdString>;

/**
 * Transforms a string into a MongoDB ObjectId in a way that can be used by OpenAPI
 */
export const ObjectIdString = z
    .string()
    .refine((val) => mongoose.Types.ObjectId.isValid(val), {
        message: "Invalid ObjectId format",
    })
    .transform((value) => new mongoose.Types.ObjectId(value))
    .openapi({
        type: "string",
        description: "MongoDB ObjectId (24-character hex string)",
        example: "507f1f77bcf86cd799439011",
    });

/**
 * Zod schema for an ObjectId as 24 hex digits, in either case. Stricter than {@link ObjectIdString}, which
 * accepts any 12-character string, and stays a string.
 */
export const ObjectIdHexString = z
    .string()
    .regex(/^[0-9a-f]{24}$/i)
    .openapi({
        type: "string",
        pattern: "^[0-9a-fA-F]{24}$",
        description: "MongoDB ObjectId (24-character hex string)",
        example: "507f1f77bcf86cd799439011",
    });

const storedLoraEuiPattern = /^eui-[0-9a-f]{16}$/;

/**
 * Zod schema for node_id values: literal prefix `eui-` plus 16 lowercase hex digits (21 characters).
 */
export const NodeIdString = z
    .string()
    .regex(storedLoraEuiPattern, {
        message: "Must be eui- followed by 16 lowercase hexadecimal digits",
    })
    .openapi({
        type: "string",
        pattern: "^eui-[a-f0-9]{16}$",
        description: "node id (eui- + 16 hex digits)",
        example: "eui-0004a30b001cd4e5",
    });

/** Values accepted by {@link NodeIdString} (use for `node_id` fields on schemas). */
export type NodeId = z.infer<typeof NodeIdString>;

/**
 * Zod schema for a LoRa EUI supplied by a caller. Accepts the EUI with or without the `eui-` prefix,
 * with separators and in any case, normalizes it, and then holds it to the stored form.
 */
export const LoraEuiString = z
    .string()
    .transform((value) => toLoraEui(value))
    .refine((value) => NodeIdString.validate(value), {
        message: "Must be a LoRa EUI: 16 hexadecimal digits, optionally prefixed with eui- and separated",
    })
    .openapi({
        type: "string",
        description: "LoRa EUI (16 hex digits; eui- prefix and separators optional)",
        example: "eui-0004a30b001cd4e5",
    });

const verdiIdMessage =
    "Must be a verdiId: a single-letter type prefix, a consonant-vowel-consonant syllable and a " +
    "3-4 digit number, as in D-SAP-300";

/**
 * Zod schema for a verdiId: a single-letter asset-type prefix, a pronounceable consonant-vowel-consonant
 * syllable, and a 3-4 digit number. Uppercased first, so a lowercase id from a URL still resolves.
 */
export const VerdiIdString = z
    .string()
    .toUpperCase()
    .regex(verdiIdRegex, {
        message: verdiIdMessage,
    })
    .openapi({
        type: "string",
        pattern: verdiIdRegex.source,
        description: "Verdi asset identifier (type prefix, CVC syllable, number)",
        example: "D-SAP-300",
    });

/** Values accepted by {@link VerdiIdString}. */
export type VerdiId = z.infer<typeof VerdiIdString>;

/**
 * Zod schema for a verdiId in its stored, uppercase form. Unlike {@link VerdiIdString} it does not
 * uppercase first, so a lowercase id is rejected.
 */
export const StoredVerdiIdString = z
    .string()
    .regex(verdiIdRegex, {
        message: verdiIdMessage,
    })
    .openapi({
        type: "string",
        pattern: verdiIdRegex.source,
        description: "Verdi asset identifier (type prefix, CVC syllable, number), uppercase",
        example: "D-SAP-300",
    });
